import React, { useEffect, useRef, useState } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'

import { Html5Qrcode } from 'html5-qrcode'
import ManualQRInput from './ManualQRInput'

interface QRScannerModalProps {
  isOpen: boolean
  onClose: () => void
  onScanSuccess: (qrData: string) => void
}

const QRScannerModal: React.FC<QRScannerModalProps> = ({
  isOpen,
  onClose,
  onScanSuccess,
}) => {
  const { colors } = useTheme()
  const { deviceType, screenWidth, screenHeight } = useDeviceDetection()
  const qrScannerRef = useRef<Html5Qrcode | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [hasScanned, setHasScanned] = useState(false)
  const [showManualInput, setShowManualInput] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [scannerInitialized, setScannerInitialized] = useState(false)

  // Inicializar scanner de QR
  useEffect(() => {
    if (!isOpen || hasScanned) return

    const initScanner = async () => {
      try {
        setCameraError(null)

        // Crear instancia del scanner
        const scanner = new Html5Qrcode('qr-scanner-container', {
          formFactor: 'portrait', // Optimizado para móviles
          aspectRatio: 1.0, // Cuadrado
        })

        qrScannerRef.current = scanner

        // Configurar opciones de escaneo
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 }, // Cuadrado de detección
          aspectRatio: 1.0,
          disableFlip: false,
        }

        // Iniciar escaneo
        await scanner.start(
          { facingMode: 'environment' }, // Cámara trasera
          config,
          (decodedText) => {
            // QR detectado exitosamente
            setHasScanned(true)
            setIsScanning(false)
            onScanSuccess(decodedText)
            scanner.stop()
            toastSuccess('QR escaneado exitosamente')
            handleClose()
          },
          (error) => {
            // Error silencioso durante el escaneo (normal) - no loguear
          }
        )

        setIsScanning(true)
        setScannerInitialized(true)
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Error desconocido'

        // Detectar tipo de error y mostrar mensaje apropiado
        let displayError = 'No se pudo acceder a la cámara'
        if (errorMsg.includes('NotFoundError')) {
          displayError = 'No hay cámara disponible en este dispositivo'
        } else if (errorMsg.includes('NotAllowedError')) {
          displayError = 'Permiso de cámara denegado. Habilita el acceso en la configuración del navegador'
        } else if (errorMsg.includes('NotReadableError')) {
          displayError = 'La cámara está siendo usada por otra aplicación'
        }

        setCameraError(displayError)
        setScannerInitialized(false)
      }
    }

    initScanner()

    return () => {
      stopScanning()
    }
  }, [isOpen, hasScanned, onScanSuccess])

  const stopScanning = async () => {
    if (qrScannerRef.current && scannerInitialized) {
      try {
        await qrScannerRef.current.stop()
      } catch (error) {
        // Error al detener es normal si nunca se inició completamente
        // No loguear para evitar ruido en consola
      }
    }
    setIsScanning(false)
    setScannerInitialized(false)
  }

  const handleClose = () => {
    stopScanning()
    setHasScanned(false)
    onClose()
  }

  const handleManualInput = (qrData: string) => {
    setIsValidating(true)
    // Simular pequeño delay para validación
    setTimeout(() => {
      onScanSuccess(qrData)
      setShowManualInput(false)
      setIsValidating(false)
      handleClose()
    }, 500)
  }

  if (!isOpen) return null

  // Calcular dimensiones dinámicas basado en deviceType
  const isMobileDevice = deviceType === 'mobile'
  const isTabletDevice = deviceType === 'tablet'

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        {/* Modal: Fullscreen en móvil, modal normal en tablet/desktop */}
        <div
          className="bg-white overflow-y-auto flex flex-col rounded-lg shadow-2xl"
          style={{
            width: isMobileDevice ? '100%' : '100%',
            maxWidth: isMobileDevice ? '100%' : '448px', // max-w-md = 448px
            height: isMobileDevice ? '100vh' : 'auto',
            maxHeight: isMobileDevice ? '100vh' : '90vh',
            padding: isMobileDevice ? '12px' : '24px',
            borderRadius: isMobileDevice ? '0px' : '8px',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2
              className={`font-bold text-gray-900`}
              style={{
                fontSize: isMobileDevice ? '20px' : '24px',
              }}
            >
              📱 Escanear QR
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              style={{
                fontSize: isMobileDevice ? '20px' : '24px',
              }}
            >
              ✕
            </button>
          </div>

          {/* Scanner o Error */}
          <div className="flex-1 mb-4 flex flex-col">
            {cameraError ? (
              <div
                className="bg-red-50 border border-red-200 rounded-lg text-center flex flex-col justify-center"
                style={{
                  padding: isMobileDevice ? '16px' : '24px',
                }}
              >
                <p
                  className="text-red-600 mb-2 font-medium"
                  style={{
                    fontSize: isMobileDevice ? '14px' : '16px',
                  }}
                >
                  {cameraError}
                </p>
                <p
                  className="text-gray-600 mb-4"
                  style={{
                    fontSize: isMobileDevice ? '12px' : '14px',
                  }}
                >
                  Asegúrate de permitir el acceso a la cámara en tu navegador.
                </p>
                <button
                  onClick={() => setShowManualInput(true)}
                  className="text-blue-600 font-medium hover:underline"
                  style={{
                    fontSize: isMobileDevice ? '14px' : '16px',
                  }}
                >
                  Usar entrada manual
                </button>
              </div>
            ) : (
              <>
                {/* Contenedor del scanner - ocupa todo el espacio disponible */}
                <div
                  id="qr-scanner-container"
                  className="w-full bg-black rounded-lg overflow-hidden flex-1"
                  style={{
                    minHeight: isMobileDevice ? '200px' : '300px',
                  }}
                />

                {/* Indicador de escaneo */}
                {isScanning && (
                  <div className="text-center mt-3">
                    <div
                      className="inline-flex items-center gap-2 bg-gray-100 rounded-full"
                      style={{
                        padding: isMobileDevice ? '8px 12px' : '12px 16px',
                      }}
                    >
                      <div
                        className="rounded-full animate-pulse"
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: colors.primary,
                        }}
                      />
                      <span
                        className="font-medium"
                        style={{
                          fontSize: isMobileDevice ? '13px' : '14px',
                        }}
                      >
                        Escaneando...
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Información */}
          <div
            className="bg-blue-50 border border-blue-200 rounded-lg mb-4 text-gray-700 leading-relaxed"
            style={{
              padding: isMobileDevice ? '12px' : '16px',
              fontSize: isMobileDevice ? '12px' : '14px',
            }}
          >
            💡 Posiciona el QR dentro del cuadrado. La cámara detectará automáticamente.
          </div>

          {/* Botones */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowManualInput(true)}
              className="flex-1 rounded-lg font-medium transition-colors text-gray-700 border border-gray-300 hover:bg-gray-50"
              style={{
                padding: isMobileDevice ? '12px' : '8px',
                fontSize: isMobileDevice ? '14px' : '12px',
              }}
            >
              ✏️ Manual
            </button>
            <button
              onClick={handleClose}
              className="flex-1 rounded-lg font-medium text-white transition-colors"
              style={{
                padding: isMobileDevice ? '12px' : '8px',
                fontSize: isMobileDevice ? '14px' : '12px',
                backgroundColor: colors.primaryDark,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1'
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modal de entrada manual */}
      <ManualQRInput
        isOpen={showManualInput}
        onClose={() => setShowManualInput(false)}
        onSubmit={handleManualInput}
        isLoading={isValidating}
      />
    </>
  )
}

export default QRScannerModal
